import paymentMethodTypes from 'common/constants/paymentMethodTypes'
import { PricePlanTypeEnum } from 'common/enums/PricePlanTypeEnum'
import { usePayment } from 'publisher/store'
import paymentSelectors, {
  getActivePricePlan,
  getOfferCurrency,
} from 'publisher/store/payment/paymentSelectors'
import { useMemo } from 'react'

type PaypalUpsellOptions = {
  clientId: string
  currency: string
  vault?: true
  intent?: 'subscription'
}

type PaypalUpsellScriptConfig = {
  options: PaypalUpsellOptions
  key: string
}

export const usePaypalUpsellOptions = (): PaypalUpsellScriptConfig | null => {
  const paymentMethod = usePayment(paymentSelectors.getActivePaymentMethod)
  const pricePlan = usePayment(getActivePricePlan)
  const currency = usePayment(getOfferCurrency)
  const isPaypalRest = paymentMethod === paymentMethodTypes.PAYPAL_REST

  return useMemo(() => {
    // Important: PayPal SDK script is loaded based on initial options.
    // If we load it before `pricePlan` is known, subscription pages may load
    // the SDK without `vault=true`, causing `createSubscription` to crash.
    if (!isPaypalRest) {
      return null
    }

    const isSubscriptionPricePlan =
      pricePlan && pricePlan.type !== PricePlanTypeEnum.OneShot
    const pricePlanKeyPart = String(pricePlan?.id)

    const options: PaypalUpsellOptions = {
      clientId: `${process.env.PAYPAL_CLIENT_ID}`,
      currency: currency.toLocaleUpperCase(),
    }

    if (isSubscriptionPricePlan) {
      const subscriptionOptions: PaypalUpsellOptions = {
        ...options,
        vault: true,
        intent: 'subscription',
      }

      return {
        options: subscriptionOptions,
        key: `paypal-rest-sub-${subscriptionOptions.currency}-${pricePlanKeyPart}`,
      }
    }

    return {
      options,
      key: `paypal-rest-order-${options.currency}-${pricePlanKeyPart}`,
    }
  }, [currency, isPaypalRest, pricePlan])
}
